<template>
  <div>
    <v-toolbar flat>
      <v-toolbar-title>Detailansicht</v-toolbar-title>
      <v-spacer />

      <v-dialog
        v-model="showExportDialog"
        :max-width="dialogWidth"
        persistent
      >
         <v-card>
          <v-card-title class="headline">
            Liste Exportieren
          </v-card-title>
         <v-card-text>
          <table class="taskStateTable">
           <tr v-if="!exportError && taskState.length === 0">
             <td> <v-icon class="spin-animation"> mdi-autorenew </v-icon> </td>
             <td> Vorgänge werden geladen... </td>
           </tr>
           <tr v-if="exportError"> <td> {{ exportError }} </td> </tr>
           <tr v-for="(task, index) in taskState" :key="task.id"
               :class="taskStateClass(task.state)">
             <td> <v-icon :class="{'spin-animation': task.state === 'STARTED'}"> {{ taskStateIcon(task.state) }} </v-icon> </td>
             <td> {{ taskNames[index] }} </td>
             <td> {{ task.error }} </td>
           </tr>
          </table>
         </v-card-text>

        <v-card-actions>
           <v-spacer />
            <v-btn
              text
              @click="closeExportDialog(true)"
            >
              Abbrechen
            </v-btn>
          </v-card-actions>
         </v-card>
      </v-dialog>

      <v-toolbar-items>
        <v-btn
          icon
          title="Excel-Tabelle herunterladen"
          :disabled="loading || !items.length"
          @click="openExportDialog"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-btn
          icon
          title="Aktualisieren"
          :disabled="loading"
          @click="get"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{'items-per-page-options': [100, 500, 1000, -1]}"
      fixed-header
      :height="dataTableHeight"
    >
      <template v-slot:item="props">
        <tr>
          <td
            v-for="header in headers"
            :key="header.value"
            :class="header.class"
          >
            {{ formatValue(header.value, props.item[header.value]) }}
          </td>
        </tr>
      </template>

       <template slot="body.append" v-if="items.length">
        <tr class="sumRow">
          <td
            v-for="header in headers"
            :key="header.value"
            :class="header.class"
          >
            {{ header.hasSum ? formatValue(header.value, sumRow[header.value]) : ''}}
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        Keine Daten mit den gewählten Filterkriterien.
      </template>
    </v-data-table>

    <a ref="download" :href="exportFileContent" :download="exportFileName" style="display: none"></a>

  </div>
</template>

<script>
import moment from 'moment'
import {mapGetters} from 'vuex'
import {api, endpoints} from '@/api'
import {PaginationSortingMixin} from './PaginationSortingMixin'
import {formatNumberDuration, formatStringDuration} from '../../filters/Duration'
import {formatFloat} from '../../filters/Number'
import {getEmployeeLabel} from '../../filters/Employee'

import {dialogWidth, dataTableHeight} from '../utils/sizes'

export default {
  name: "DetailsTable2",
  mixins: [PaginationSortingMixin],
  props: {
    filters: {
      type: Object,
      default: null,
    }
  },
  data () {
    return {
      loading: false,
      items: [],
      options: {
        itemsPerPage: 500
      },
      showExportDialog: false,
      taskIds: [],
      taskState: [],
      taskNames: [
        "Datensätze laden",
        "Exceldatei erstellen",
        "Spaltenbreiten anpassen"
      ],
      exportFileName: '',
      exportFileContent: '',
      exportError: '',
      dataTableHeight: dataTableHeight
    }
  },
  computed: {
    headers () {
      if (this.selectedTenantObj === null) {
        return []
      }
      const fields = this.selectedTenantObj.log_fields.lists
      return fields.map(i => ({text: this.getDisplayName(i), value: i, class: this.getClass(i), hasSum: this.hasSum(i)}))
    },
    sumRow () {
      let result = {}
      for(let h of this.headers) {
        if(h.hasSum){
          result[h.value] = this.items.reduce((accumulated, currentItem) => accumulated + Number(currentItem[h.value]), 0)
        }
        else {
          result[h.value] = null
        }
      }
      return result
    },
    dialogWidth: dialogWidth,
    ...mapGetters(['selectedTenantObj', 'profile']),
  },
  watch: {
    filters: {
      handler () {
        this.get()
      },
      immediate: true,
    },
  },
  methods: {
    async get () {
      this.loading = true
      try {
        const r = await api(endpoints.fetch_log(), {...this.filters, ...this.paginationOptions, ...this.sortingOptions}, {method: 'GET'})
        if (r.ok) {
          this.items.splice(0, this.items.length, ...await r.json())
          this.total = this.getTotal(r) || this.items.length
        } else {
          // TODO: error handling
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.loading = false
      }
    },
    getDisplayName (fieldName) {
      switch (fieldName) {
        case 'description':
          return 'Beschreibung'
        case 'project':
          return 'Projekt'
        case 'employee':
          return 'Mitarbeiter'
        case 'taskduration':
          return 'Arbeitszeit'
        case 'taskday':
          return 'Datum'
        case 'journey':
          return 'Reisedistanz'
        case 'journey_duration':
          return 'Reisezeit'
        case 'place':
          return 'Ort'
        case 'additional_meal_allowance':
          return 'Auslösung'
        case 'operation':
          return 'Tätigkeit'
      }
    },
    getClass (fieldName) {
      switch (fieldName) {
        case 'taskduration':
        case 'taskday':
        case 'journey':
        case 'journey_duration':
        case 'additional_meal_allowance':
          return "text-right"
        case 'description':
        case 'project':
        case 'employee':
        case 'place':
        case 'operation':
          return "text-left"
      }
    },
    hasSum (fieldName) {
      switch (fieldName) {
        case 'taskduration':
        case 'journey':
        case 'journey_duration':
        case 'additional_meal_allowance':
          return true
        case 'taskday':
        case 'description':
        case 'project':
        case 'employee':
        case 'place':
        case 'operation':
          return false
      }
    },
    formatValue (fieldName, value) {
      switch (fieldName) {
        case 'description':
          return value
        case 'project':
          return value !== null ? value.name : ''
        case 'employee':
          return value !== null ? getEmployeeLabel(value) : ''
        case 'taskduration':
          return this.profile.format_hour_as_number ? formatNumberDuration(value) : formatStringDuration(value)
        case 'taskday':
          return moment(value).format(this.profile.format_date_with_day ? 'dd, DD.MM.YYYY' : 'DD.MM.YYYY')
        case 'journey':
          return value  // TODO: number format
        case 'journey_duration':
          return this.profile.format_hour_as_number ? formatNumberDuration(value) : formatStringDuration(value)
        case 'place':
          return value
        case 'additional_meal_allowance':
          return formatFloat(Number(value)) + ' €'
        case 'operation':
          return value !== null ? value.name : ''
      }
    },
    taskStateIcon (state) {
      switch (state) {
        case 'STARTED':
          return 'mdi-autorenew'
        case 'PENDING':
          return 'mdi-dots-vertical-circle-outline'
        case 'SUCCESS':
          return 'mdi-check-circle-outline'
        case 'FAILURE':
        case 'REVOKED':
          return 'mdi-alert-circle-outline'
      }
    },
    taskStateClass (state) {
      switch (state) {
        case 'STARTED':
          return 'taskStarted'
        case 'PENDING':
          return 'taskPending'
        case 'SUCCESS':
          return ''
        case 'FAILURE':
        case 'REVOKED':
          return 'taskFailure'
      }
    },
    async progress (interval) {
      this.taskIds.splice(0)
      this.taskState.splice(0)

      const checkProgress = async (resolve, reject) => {
        if (!this.showExportDialog) {
          reject()
          return
        }

        let params = {}
        if (this.taskIds.length < 1) {
          // start export
         params = {...this.filters, ...this.sortingOptions}
        } else {
          // check task states
         params = {"task-ids": JSON.stringify(this.taskIds)}
        }

        const r = await api(endpoints.fetch_details(), params, {method: 'GET'})
        if (r.ok) {
          let response_data = await r.json()
          this.taskIds.splice(0, this.taskIds.length, ...response_data["task-ids"])
          this.taskState.splice(0, this.taskState.length, ...response_data["tasks"].reverse())

          if (response_data["failed"]) {
            reject()
          }
          else if (response_data["success"]) {
            resolve(response_data["result"])
          }
          else {
            setTimeout(checkProgress, interval, resolve, reject)
          }
        }
        else {
          reject("Serverfehler")
        }
      }
      return new Promise(checkProgress)
    },
    async openExportDialog () {
      this.exportError = ""
      this.showExportDialog = true

      this.progress(1000).then(result => {
        this.exportFileContent = "data:application/vnd.ms-excel;base64," + result
        this.exportFileName = "Lohn_Detailansicht.xlsx"
        this.$nextTick(() => {
          this.$refs.download.click()
          this.closeExportDialog(false)
        });
      }).catch(err => {
        this.exportError = err
      })
    },
    closeExportDialog (revoke) {
      console.log(revoke)
      this.showExportDialog = false
      if (this.taskIds.length > 0 && revoke) {
        api(endpoints.fetch_details(), {"task-ids": JSON.stringify(this.taskIds), revoke: true}, {method: 'GET'})
      }
      this.taskIds.splice(0)
    }
  }
}
</script>

<style lang="scss">
.sumRow {
  font-weight: bold;
  border-top-style: double;
  border-top-color: lightgray;
}

.taskStateTable td {
  padding:5px;
}

.taskPending, taskPending .v-icon {
  opacity: 0.5;
}

.taskStarted, .taskStarted .v-icon {
  font-weight: bold;
}

.taskFailure, .taskFailure .v-icon {
  font-weight: bold;
  color: red;
}
</style>
